import { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import CustomCheckbox from '@components/ui/CustomCheckbox';
import InputBoxWithUnit from '@/components/ui/InputBoxWithUnit';
import Spinner from '@components/ui/Spinner';
import type { FormProps } from '../../types/form.types';
import { useTheme } from '@theme/useTheme';

export interface Page2Handle {
  validate: () => boolean;
}

const Page2 = forwardRef<Page2Handle, FormProps>(({
  formData,
  updateFormData,
  onPrevious,
  onNext,
  currentPage = 2,
  canGoPrevious = true,
  canGoNext = true,
  isLoading = false,
  error,
}, ref) => {
  const { colors } = useTheme();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Refs for each field
  const snoringRef = useRef<HTMLDivElement>(null);
  const witnessedApneaRef = useRef<HTMLDivElement>(null);
  const breathingStopsRef = useRef<HTMLDivElement>(null);
  const morningHeadachesRef = useRef<HTMLDivElement>(null);
  const morningDryMouthRef = useRef<HTMLDivElement>(null);
  const daytimeFatigueRef = useRef<HTMLDivElement>(null);

  // Scroll to first error when errors change
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      
      const fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
        snoring: snoringRef,
        witnessedApnea: witnessedApneaRef,
        breathingStops: breathingStopsRef,
        morningHeadaches: morningHeadachesRef,
        morningDryMouth: morningDryMouthRef,
        daytimeFatigue: daytimeFatigueRef,
      };

      const refToScroll = fieldRefs[firstErrorField];
      if (refToScroll?.current) {
        refToScroll.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [errors]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required Yes/No questions
    if (!formData.snoring) {
      newErrors.snoring = 'Please select an option';
    }
    
    if (!formData.witnessedApnea) {
      newErrors.witnessedApnea = 'Please select an option';
    }
    
    if (!formData.breathingStops) {
      newErrors.breathingStops = 'Please select an option';
    }
    
    if (!formData.morningHeadaches) {
      newErrors.morningHeadaches = 'Please select an option';
    }
    
    if (!formData.morningDryMouth) {
      newErrors.morningDryMouth = 'Please select an option';
    }

    // Daytime Fatigue validation (1-10 scale)
    if (formData.daytimeFatigue === null || formData.daytimeFatigue === undefined) {
      newErrors.daytimeFatigue = 'Daytime fatigue rating is required';
    } else {
      const fatigueValue = formData.daytimeFatigue;
      if (fatigueValue < 1 || fatigueValue > 10) {
        newErrors.daytimeFatigue = 'Daytime fatigue must be between 1 and 10';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleYesNoChange = (field: keyof typeof formData, value: string) => {
    updateFormData({ [field]: value });
    clearError(field as string);
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div
        className="border p-6 sm:p-8 lg:p-10 overflow-hidden"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          borderTopRightRadius: '2rem',
          borderBottomLeftRadius: '2rem'
        }}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6" style={{ color: colors.heading }}>
          Symptoms & Cognitive Assessment
        </h2>
        <p className="mb-6 sm:mb-8 text-base sm:text-lg" style={{ color: colors.text }}>
          All fields marked with <span className="font-bold" style={{ color: colors.error }}>*</span> are required
        </p>

        <div className="space-y-4 sm:space-y-6">
          <div ref={snoringRef}>
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Do you snore loudly? <span style={{ color: colors.error }}>*</span>
            </label>
            <div className="space-y-2">
              {['Yes', 'No'].map((option) => (
                <CustomCheckbox
                  key={option}
                  description={option}
                  checked={formData.snoring === option}
                  onChange={() => handleYesNoChange('snoring', option)}
                />
              ))}
            </div>
            {errors.snoring && (
              <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.snoring}</p>
            )}
          </div>

          <div ref={witnessedApneaRef}>
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Has anyone witnessed you stop breathing during sleep? <span style={{ color: colors.error }}>*</span>
            </label>
            <div className="space-y-2">
              {['Yes', 'No'].map((option) => (
                <CustomCheckbox
                  key={option}
                  description={option}
                  checked={formData.witnessedApnea === option}
                  onChange={() => handleYesNoChange('witnessedApnea', option)}
                />
              ))}
            </div>
            {errors.witnessedApnea && (
              <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.witnessedApnea}</p>
            )}
          </div>

          <div ref={breathingStopsRef}>
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Do you experience episodes where your breathing stops during sleep? <span style={{ color: colors.error }}>*</span>
            </label>
            <div className="space-y-2">
              {['Yes', 'No'].map((option) => (
                <CustomCheckbox
                  key={option}
                  description={option}
                  checked={formData.breathingStops === option}
                  onChange={() => handleYesNoChange('breathingStops', option)}
                />
              ))}
            </div>
            {errors.breathingStops && (
              <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.breathingStops}</p>
            )}
          </div>

          <div ref={morningHeadachesRef}>
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Do you wake up with headaches? <span style={{ color: colors.error }}>*</span>
            </label>
            <div className="space-y-2">
              {['Yes', 'No'].map((option) => (
                <CustomCheckbox
                  key={option}
                  description={option}
                  checked={formData.morningHeadaches === option}
                  onChange={() => handleYesNoChange('morningHeadaches', option)}
                />
              ))}
            </div>
            {errors.morningHeadaches && (
              <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.morningHeadaches}</p>
            )}
          </div>

          <div ref={morningDryMouthRef}>
            <label className="block text-sm font-medium mb-3" style={{ color: colors.text }}>
              Do you wake up with a dry mouth? <span style={{ color: colors.error }}>*</span>
            </label>
            <div className="space-y-2">
              {['Yes', 'No'].map((option) => (
                <CustomCheckbox
                  key={option}
                  description={option}
                  checked={formData.morningDryMouth === option}
                  onChange={() => handleYesNoChange('morningDryMouth', option)}
                />
              ))}
            </div>
            {errors.morningDryMouth && (
              <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.morningDryMouth}</p>
            )}
          </div>

          <div ref={daytimeFatigueRef}>
            <InputBoxWithUnit
              beforeText="Daytime Fatigue Level"
              unitValue="(1-10 scale)"
              isSecureText={false}
              value={formData.daytimeFatigue?.toString() || ''}
              onChange={(text) => {
                const parsed = parseInt(text, 10);
                updateFormData({ daytimeFatigue: isNaN(parsed) ? null : parsed });
                clearError('daytimeFatigue');
              }}
              keyBoardType="numeric"
              hasError={!!errors.daytimeFatigue}
              errorMessage={errors.daytimeFatigue}
              required
              placeholder="Enter a number from 1 to 10"
              description="Rate your daytime fatigue level: 1-3 (Minimal), 4-6 (Moderate), 7-8 (Significant), 9-10 (Severe)"
            />
          </div>
        </div>

        <div 
          className="mt-8 p-4 sm:p-6 rounded-lg" 
          style={{ 
            backgroundColor: colors.secondaryButton,
            borderTopRightRadius: '1.5rem',
            borderBottomLeftRadius: '1.5rem'
          }}
        >
          <h3 className="font-semibold mb-3 text-base sm:text-lg" style={{ color: colors.heading }}>
            Understanding Sleep Apnea Symptoms:
          </h3>
          <ul className="text-sm sm:text-base space-y-2" style={{ color: colors.text }}>
            <li>• <strong>Snoring:</strong> Loud, persistent snoring is a key indicator of airway obstruction</li>
            <li>• <strong>Breathing Pauses:</strong> Episodes of stopped breathing during sleep indicate apnea</li>
            <li>• <strong>Morning Symptoms:</strong> Headaches and dry mouth suggest poor sleep quality</li>
            <li>• <strong>Daytime Fatigue:</strong> Excessive tiredness affects daily functioning and safety</li>
          </ul>
        </div>

        {/* Navigation Buttons */}
        {onPrevious && onNext && (
          <div
            className="mt-6 sm:mt-8 pt-6 border-t"
            style={{ borderColor: colors.border }}
          >
            {/* Error Display */}
            {error && (
              <div
                className="mb-4 p-3 rounded-lg border"
                style={{
                  backgroundColor: colors.error + '20',
                  borderColor: colors.error
                }}
              >
                <p className="text-sm" style={{ color: colors.error }}>{error}</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <button
                onClick={onPrevious}
                disabled={!canGoPrevious || isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium transition-all duration-200 ${canGoPrevious && !isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: canGoPrevious && !isLoading ? colors.secondaryButton : colors.input,
                  color: canGoPrevious && !isLoading ? colors.text : colors.text + '80',
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                Previous
              </button>

              {/* Page Indicators - Centered */}
              <div className="flex items-center justify-center space-x-2">
                {[0, 1, 2, 3].map((page) => (
                  <div
                    key={page}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${page === currentPage
                      ? 'scale-110'
                      : page < currentPage
                        ? 'opacity-60'
                        : 'opacity-30'
                      }`}
                    style={{
                      backgroundColor:
                        page === currentPage
                          ? colors.primary
                          : page < currentPage
                            ? colors.primary
                            : colors.border,
                    }}
                  />
                ))}
              </div>

              <button
                onClick={onNext}
                disabled={!canGoNext || isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium text-white transition-all duration-200 ${canGoNext && !isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: canGoNext && !isLoading ? colors.primaryButton : colors.border,
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                {isLoading ? (
                  <Spinner size="sm" color="white" text="Processing..." showText={true} />
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

Page2.displayName = 'Page2';

export default Page2;
